import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Image as ImageIcon, Upload, Palette } from 'lucide-react';
import BranchesManager from '../components/BranchesManager';
import RichText from '../components/RichText';
import { defaultSiteContent } from '../portal/defaultContent';

interface Sizes {
  headerLogoHeight: number;
  footerLogoHeight: number;
  systemLogoHeight: number;
  loginLogoWidth: number;
  receiptLogoHeight: number;
  watermarkScale: number;
  watermarkOpacity: number;
}

interface Branding {
  logoUrl?: string | null;
  watermarkUrl?: string | null;
  faviconUrl?: string | null;
  receiptUrl?: string | null;
  sizes?: Sizes;
}

const DEFAULT_SIZES: Sizes = {
  headerLogoHeight: 48,
  footerLogoHeight: 48,
  systemLogoHeight: 72,
  loginLogoWidth: 320,
  receiptLogoHeight: 110,
  watermarkScale: 40,
  watermarkOpacity: 25,
};

export default function SettingsPage() {
  const [branding, setBranding] = useState<Branding>({});
  const [busy, setBusy] = useState<'logo' | 'watermark' | 'favicon' | 'receipt' | null>(null);
  const [sizes, setSizes] = useState<Sizes>(DEFAULT_SIZES);
  const [savingSizes, setSavingSizes] = useState(false);
  const [content, setContent] = useState<any>({});
  const [savingContent, setSavingContent] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const wmRef = useRef<HTMLInputElement>(null);
  const favRef = useRef<HTMLInputElement>(null);
  const receiptRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const { data } = await api.get('/branding');
      setBranding(data || {});
      if (data?.sizes) setSizes({ ...DEFAULT_SIZES, ...data.sizes });
    } catch {
      /* ignore */
    }
  }
  useEffect(() => {
    load();
    api
      .get('/public/site')
      .then(({ data }) => {
        const d = defaultSiteContent(data?.name, data?.email);
        const c = data?.content || {};
        // Já preenche com os textos prontos quando ainda não há conteúdo salvo.
        setContent({
          about: c.about || d.about,
          privacy: c.privacy || d.privacy,
          cookies: c.cookies || d.cookies,
          terms: c.terms || d.terms,
          permuta: c.permuta || '',
          dpoName: c.dpoName || '',
          dpoEmail: c.dpoEmail || '',
          businessHours: c.businessHours || '',
        });
      })
      .catch(() => undefined);
  }, []);

  function setC(k: string, v: string) {
    setContent((c: any) => ({ ...c, [k]: v }));
  }
  async function saveContent() {
    setSavingContent(true);
    try {
      const { data } = await api.post('/public/site-content', content);
      if (data) setContent(data);
      alert('Conteúdo do site salvo. Recarregue o portal (Ctrl+Shift+R) para ver.');
    } catch {
      alert('Falha ao salvar o conteúdo.');
    } finally {
      setSavingContent(false);
    }
  }

  async function saveSizes() {
    setSavingSizes(true);
    try {
      const { data } = await api.post('/branding/sizes', sizes);
      if (data) setSizes({ ...DEFAULT_SIZES, ...data });
      alert('Tamanhos salvos. Recarregue as páginas (Ctrl+Shift+R) para ver.');
    } catch {
      alert('Falha ao salvar os tamanhos.');
    } finally {
      setSavingSizes(false);
    }
  }

  async function upload(kind: 'logo' | 'watermark' | 'favicon' | 'receipt', file?: File | null) {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('access_token');
    const base = api.defaults.baseURL || '/api';
    setBusy(kind);
    try {
      const r = await fetch(`${base}/branding/${kind}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!r.ok) throw new Error('upload');
      await load();
      alert('Imagem atualizada. Recarregue a página (Ctrl+Shift+R) para ver.');
    } catch {
      alert('Falha no upload. Use uma imagem (PNG/JPG/SVG).');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold dark:text-dark-text-primary flex items-center gap-2">
          <Palette className="w-6 h-6" /> Configurações
        </h1>
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
          Dados da imobiliária e filiais, logos (sistema, marca d'água e favicon) usados no
          sistema, recibos e fotos.
        </p>
      </div>

      <BranchesManager />

      {/* Feed para portais (anúncios automáticos) */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-1 dark:text-dark-text-primary flex items-center gap-2">
          <Upload className="w-5 h-5" /> Feed de anúncios (ZAP / VivaReal / OLX)
        </p>
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-3">
          Cole esta URL no painel do portal imobiliário (formato VRSync). Todos os imóveis
          <strong> disponíveis e com foto</strong> são publicados automaticamente — sem login, sem trabalho manual.
        </p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={`${window.location.origin}/api/public/feed/vrsync`}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-sm bg-gray-50 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
          />
          <button
            onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/api/public/feed/vrsync`);
              alert('URL do feed copiada.');
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm whitespace-nowrap"
          >
            Copiar
          </button>
        </div>
      </div>

      {/* Tamanhos das imagens */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-1 dark:text-dark-text-primary flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Tamanho das imagens
        </p>
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-4">
          Defina o tamanho de cada logo e da marca d'água. Valores em pixels (px); a marca d'água em %.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {([
            ['headerLogoHeight', 'Logo do site — topo (altura px)'],
            ['footerLogoHeight', 'Logo do site — rodapé (altura px)'],
            ['systemLogoHeight', 'Logo do sistema/menu (altura px)'],
            ['loginLogoWidth', 'Logo da tela de login (largura px)'],
            ['receiptLogoHeight', 'Logo de recibos/impressão (altura px)'],
            ['watermarkScale', "Marca d'água — tamanho (% da foto)"],
            ['watermarkOpacity', "Marca d'água — opacidade (%)"],
          ] as [keyof Sizes, string][]).map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-xs text-gray-500 dark:text-dark-text-secondary">{label}</span>
              <input
                type="number"
                min={1}
                value={sizes[key]}
                onChange={(e) => setSizes((s) => ({ ...s, [key]: Number(e.target.value) }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              />
            </label>
          ))}
        </div>
        <div className="mt-4">
          <button
            onClick={saveSizes}
            disabled={savingSizes}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
          >
            {savingSizes ? 'Salvando...' : 'Salvar tamanhos'}
          </button>
        </div>
      </div>

      {/* Conteúdo do site / Políticas */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-1 dark:text-dark-text-primary flex items-center gap-2">
          <Palette className="w-5 h-5" /> Conteúdo do site e políticas
        </p>
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-4">
          Textos exibidos no portal público. Deixe em branco para usar o texto padrão.
        </p>
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <label className="block text-sm dark:text-dark-text-primary">Encarregado / DPO (nome)
              <input value={content.dpoName || ''} onChange={(e) => setC('dpoName', e.target.value)} className="input mt-1" placeholder="Nome do responsável (LGPD)" />
            </label>
            <label className="block text-sm dark:text-dark-text-primary">E-mail do DPO
              <input value={content.dpoEmail || ''} onChange={(e) => setC('dpoEmail', e.target.value)} className="input mt-1" placeholder="dpo@suaempresa.com.br" />
            </label>
            <label className="block text-sm dark:text-dark-text-primary">Horário de atendimento
              <input value={content.businessHours || ''} onChange={(e) => setC('businessHours', e.target.value)} className="input mt-1" placeholder="Seg-Sex 8h-18h · Sáb 9h-12h" />
            </label>
          </div>
          {([
            ['about', 'Quem Somos (texto da página)'],
            ['privacy', 'Política de Privacidade (texto completo)'],
            ['cookies', 'Política de Cookies (texto completo)'],
            ['terms', 'Termos de Uso (texto completo)'],
            ['permuta', 'Cláusula de Permuta (opcional)'],
          ] as [string, string][]).map(([key, label]) => (
            <div key={key}>
              <span className="text-sm dark:text-dark-text-primary">{label}</span>
              <RichText
                value={content[key] || ''}
                onChange={(html) => setC(key, html)}
                minHeight={key === 'about' || key === 'permuta' ? 120 : 220}
              />
            </div>
          ))}
          <button onClick={saveContent} disabled={savingContent} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
            {savingContent ? 'Salvando...' : 'Salvar conteúdo do site'}
          </button>
        </div>
      </div>

      {/* Logo */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-3 dark:text-dark-text-primary flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Logo da imobiliária
        </p>
        <div className="flex items-center gap-6">
          <div className="w-48 h-24 border border-dashed border-gray-300 dark:border-dark-border rounded-lg flex items-center justify-center bg-gray-50 dark:bg-dark-bg-tertiary overflow-hidden">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-gray-400">Sem logo</span>
            )}
          </div>
          <div>
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload('logo', e.target.files?.[0])}
            />
            <button
              onClick={() => logoRef.current?.click()}
              disabled={busy === 'logo'}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
            >
              <Upload className="w-4 h-4" /> {busy === 'logo' ? 'Enviando...' : 'Enviar logo'}
            </button>
            <p className="text-xs text-gray-400 mt-2">PNG com fundo transparente recomendado.</p>
          </div>
        </div>
      </div>

      {/* Logo de impressão (recibos/documentos) */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-1 dark:text-dark-text-primary flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Logo de impressão (recibos e documentos)
        </p>
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-3">
          Logo que aparece no topo dos recibos e documentos impressos. Se não enviar, usa o logo da imobiliária.
          O tamanho é definido em "Tamanho das imagens" (Logo de recibos/impressão).
        </p>
        <div className="flex items-center gap-6">
          <div className="w-48 h-24 border border-dashed border-gray-300 dark:border-dark-border rounded-lg flex items-center justify-center bg-gray-50 dark:bg-dark-bg-tertiary overflow-hidden">
            {branding.receiptUrl ? (
              <img src={branding.receiptUrl} alt="logo recibo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-gray-400">Sem logo de recibo</span>
            )}
          </div>
          <div>
            <input
              ref={receiptRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload('receipt', e.target.files?.[0])}
            />
            <button
              onClick={() => receiptRef.current?.click()}
              disabled={busy === 'receipt'}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
            >
              <Upload className="w-4 h-4" /> {busy === 'receipt' ? 'Enviando...' : 'Enviar logo de recibo'}
            </button>
            <p className="text-xs text-gray-400 mt-2">PNG/JPG. Recomendado fundo branco ou transparente.</p>
          </div>
        </div>
      </div>

      {/* Marca d'água */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-3 dark:text-dark-text-primary flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Logo da marca d'água (fotos dos imóveis)
        </p>
        <div className="flex items-center gap-6">
          <div className="w-48 h-24 border border-dashed border-gray-300 dark:border-dark-border rounded-lg flex items-center justify-center bg-gray-50 dark:bg-dark-bg-tertiary overflow-hidden">
            {branding.watermarkUrl ? (
              <img src={branding.watermarkUrl} alt="marca d'água" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-gray-400">Sem marca d'água</span>
            )}
          </div>
          <div>
            <input
              ref={wmRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload('watermark', e.target.files?.[0])}
            />
            <button
              onClick={() => wmRef.current?.click()}
              disabled={busy === 'watermark'}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
            >
              <Upload className="w-4 h-4" /> {busy === 'watermark' ? 'Enviando...' : 'Enviar marca d\'água'}
            </button>
            <p className="text-xs text-gray-400 mt-2">PNG transparente. Aplicado centralizado (~25%) nas fotos.</p>
          </div>
        </div>
      </div>

      {/* Favicon */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
        <p className="font-semibold mb-3 dark:text-dark-text-primary flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Favicon (ícone da aba)
        </p>
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 border border-dashed border-gray-300 dark:border-dark-border rounded-lg flex items-center justify-center bg-gray-50 dark:bg-dark-bg-tertiary overflow-hidden">
            {branding.faviconUrl ? (
              <img src={branding.faviconUrl} alt="favicon" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-[10px] text-gray-400">Sem ícone</span>
            )}
          </div>
          <div>
            <input
              ref={favRef}
              type="file"
              accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
              className="hidden"
              onChange={(e) => upload('favicon', e.target.files?.[0])}
            />
            <button
              onClick={() => favRef.current?.click()}
              disabled={busy === 'favicon'}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 dark:text-dark-text-primary"
            >
              <Upload className="w-4 h-4" /> {busy === 'favicon' ? 'Enviando...' : 'Enviar favicon'}
            </button>
            <p className="text-xs text-gray-400 mt-2">Ícone quadrado (ex.: 512×512). Recarregue a página após enviar.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
